import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { Button, Badge } from './ui';
import { Link2, RefreshCw, Ban, RotateCcw, AlertTriangle, X } from 'lucide-react';
import SendLinkModal from './SendLinkModal';
import { toDisplayDate } from './Events';

// ─── Table Links (Stage 5) ────────────────────────────────────────────────────
// Every staff-issued table booking link across ALL dates, so the ones nobody
// paid are visible and their tables can be freed before the hold lapses.
// Reached from the seating sheet's toolbar ("All links"), where it opens as a
// full-screen panel over the sheet (TableLinksPanel, below) — it is a view, not
// one of the sheet's selection modes, and it never narrows to the sheet's date.
//
// Reads through list_table_links (staff-only RPC: the customer's name lives on
// `customers`, which staff logins cannot read directly). The DISPLAYED state is
// derived here from status AND expires_at, the way every reader of
// booking_holds derives liveness: nothing marks a link expired in the database
// and there is no sweeper, so a row reads 'active' long after its expires_at.
//
// Cancel → cancel_table_link (sets cancelled + releases the hold with Stage 1's
// opt-in, so the tables come free at once). Reissue = cancel, then the very
// same SendLinkModal / create-table-link path the seating sheet uses,
// prefilled with the old details and a fresh expiry. No link is ever edited
// in place.

const FILTERS = [
  { key: 'live',      label: 'Live' },
  { key: 'expired',   label: 'Expired' },
  { key: 'used',      label: 'Used' },
  { key: 'cancelled', label: 'Cancelled' },
];

const EMPTY_COPY = {
  live:      'No live links. Everything sent has been paid, lapsed or cancelled.',
  expired:   'No expired links.',
  used:      'No link has been paid yet.',
  cancelled: 'No cancelled links.',
};

// status + expires_at → what the row IS right now. 'active' past its
// expires_at is expired, whatever the column says.
export const linkState = (row, nowMs) => {
  if (row.status === 'used' || row.status === 'cancelled' || row.status === 'expired') return row.status;
  return new Date(row.expires_at).getTime() > nowMs ? 'live' : 'expired';
};

const STATE_BADGE = {
  live:      { tone: 'green', label: 'Live' },
  expired:   { tone: 'grey',  label: 'Expired' },
  used:      { tone: 'gold',  label: 'Paid' },
  cancelled: { tone: 'red',   label: 'Cancelled' },
};

// Live rows: soonest to lapse first (that is the one needing a decision).
// Everything else: most recent event first.
const sortFor = (key) => {
  const t = (v) => (v ? new Date(v).getTime() : 0);
  switch (key) {
    case 'live':      return (a, b) => t(a.expires_at) - t(b.expires_at);
    case 'expired':   return (a, b) => t(b.expires_at) - t(a.expires_at);
    case 'used':      return (a, b) => t(b.used_at || b.created_at) - t(a.used_at || a.created_at);
    case 'cancelled': return (a, b) => t(b.cancelled_at || b.created_at) - t(a.cancelled_at || a.created_at);
    default:          return (a, b) => t(b.created_at) - t(a.created_at);
  }
};

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
  : '');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }) : '');

// Remaining time, in the unit that matters at that range: hours above an
// hour, whole minutes above ten, m:ss below that.
const countdown = (ms) => {
  if (ms <= 0) return 'lapsing';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m >= 10) return `${m} min`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};
const countdownColor = (ms) => {
  if (ms < 5 * 60 * 1000) return 'var(--color-danger-strong)';
  if (ms < 30 * 60 * 1000) return 'var(--color-warning-strong)';
  return NAVY;
};

const guestsText = (r) => [
  `${r.adults} A`,
  r.children > 0 ? `${r.children} C` : null,
  r.infants > 0 ? `${r.infants} I` : null,
].filter(Boolean).join(' · ');

// PostgREST surfaces RAISE EXCEPTION as { code: 'P0001', message: '<CODE>', details }.
const RPC_MESSAGES = {
  LINK_USED:         'This link has already been paid for and booked. Cancel the booking from Bookings instead.',
  LINK_CANCELLED:    'This link was already cancelled.',
  PAYMENT_IN_FLIGHT: 'A payment for this link has just gone through and is being booked. Refresh in a moment.',
  LINK_MISSING:      'This link no longer exists.',
  NOT_STAFF:         'Only a staff login can manage links.',
};
const rpcMessage = (err, fallback) => {
  if (!err) return fallback;
  const code = (err.message || '').trim();
  if (RPC_MESSAGES[code]) return `${RPC_MESSAGES[code]}${err.details ? ` (${err.details})` : ''}`;
  return err.message || fallback;
};

// ─── Styles (Events / Enquiries conventions) ─────────────────────────────────
const card = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', marginBottom: 18, boxShadow: 'var(--shadow-card)' };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const thNum = { ...thStyle, textAlign: 'right' };
const tdStyle = { padding: '9px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'top' };
const tdNum = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };
const strong = { fontWeight: 600, color: NAVY };
const sub = { fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', marginTop: 2 };
const filterBtnStyle = (active) => ({
  padding: '7px 14px',
  minHeight: 36,
  background: active ? NAVY : 'var(--color-sunken)',
  color: active ? '#ffffff' : 'var(--color-ink-muted)',
  border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
  borderRadius: 8,
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  transition: 'background-color 0.15s ease-out, color 0.15s ease-out, border-color 0.15s ease-out',
});

export default function TableLinks({ onClose = null }) {
  const [rows, setRows] = useState(null);      // null = loading
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('live');
  const [now, setNow] = useState(() => Date.now());
  const [confirm, setConfirm] = useState(null); // { action: 'cancel' | 'reissue', row }
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [reissue, setReissue] = useState(null); // the row the SendLinkModal is prefilled from

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.rpc('list_table_links');
    if (err) { setError(rpcMessage(err, 'Could not load the links.')); setRows((r) => r || []); return; }
    setError('');
    setRows(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  // One clock for every countdown, and the tick that moves a live link into
  // Expired the second its expires_at passes — no server write involved.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const withState = useMemo(() => (rows || []).map((r) => ({ ...r, state: linkState(r, now) })), [rows, now]);
  const counts = useMemo(() => {
    const c = { live: 0, expired: 0, used: 0, cancelled: 0 };
    withState.forEach((r) => { c[r.state] = (c[r.state] || 0) + 1; });
    return c;
  }, [withState]);
  const visible = useMemo(() => withState.filter((r) => r.state === filter).sort(sortFor(filter)), [withState, filter]);

  // ── Cancel / reissue ───────────────────────────────────────────────────────
  const openModalFor = (row) => setReissue(row);

  const runConfirm = async () => {
    if (!confirm || busy) return;
    const { action, row } = confirm;
    setBusy(true); setConfirmError('');
    const { error: err } = await supabase.rpc('cancel_table_link', { p_link_id: row.id });
    setBusy(false);
    if (err) { setConfirmError(rpcMessage(err, 'The link could not be cancelled.')); load(); return; }
    setConfirm(null);
    await load();
    if (action === 'reissue') openModalFor(row);
  };

  const startReissue = (row) => {
    // A cancelled link needs no cancel step; a live or lapsed one is cancelled first.
    if (row.state === 'cancelled') openModalFor(row);
    else setConfirm({ action: 'reissue', row });
  };

  const prefillFor = (row) => {
    const differs = Math.round(Number(row.amount)) !== Math.round(Number(row.standard_amount));
    return {
      name: row.customer_name || '',
      phone: row.phone || '',
      adults: row.adults, children: row.children, infants: row.infants,
      override: differs ? String(Math.round(Number(row.amount))) : '',
      reason: differs ? (row.override_reason || '') : '',
    };
  };

  const describe = (row) => `${row.customer_name || row.phone} · ${row.table_names.join(', ')} · ${toDisplayDate(row.booking_date)} · ${row.cruise_name} · ${row.deck}`;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: NAVY, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={18} strokeWidth={2} aria-hidden="true" />Table Links
          </h2>
          <div style={{ ...sub, marginTop: 4 }}>Private booking links sent from the seating sheet, across all dates. Live links hold their tables until they expire.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={load} disabled={rows === null}>
            <RefreshCw size={14} strokeWidth={1.75} aria-hidden="true" />Refresh
          </Button>
          {onClose && (
            <Button variant="secondary" size="sm" onClick={onClose} data-autofocus="true">
              <X size={14} strokeWidth={1.75} aria-hidden="true" />Close
            </Button>
          )}
        </div>
      </div>

      <div style={card}>
        <div className="ev-filters" role="tablist" aria-label="Filter links">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} style={filterBtnStyle(filter === f.key)} onClick={() => setFilter(f.key)}>
              {f.label}{rows !== null && <span style={{ marginLeft: 6, opacity: 0.75 }}>{counts[f.key] || 0}</span>}
            </button>
          ))}
        </div>

        {error && <div className="toast toast-error" role="alert">{error}</div>}

        {rows === null ? (
          <div aria-busy="true" aria-label="Loading links">
            {[0, 1, 2].map((i) => (
              <div key={i} className="ev-skel-row">
                <span className="ev-skel" style={{ width: 120 }} />
                <span className="ev-skel" style={{ flex: 1 }} />
                <span className="ev-skel" style={{ width: 90 }} />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-ink-muted)', fontSize: '0.9rem' }}>
            {EMPTY_COPY[filter]}
          </div>
        ) : (
          <div style={tableWrap}>
            <table className="data-table" style={tblStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Sailing</th>
                  <th style={thStyle}>Tables</th>
                  <th style={thStyle}>Guests</th>
                  <th style={thNum}>Amount</th>
                  <th style={thStyle}>{filter === 'live' ? 'Expires in' : filter === 'expired' ? 'Expired' : filter === 'used' ? 'Paid' : 'Cancelled'}</th>
                  <th style={thStyle}>State</th>
                  <th style={{ ...thStyle, width: 1 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const badge = STATE_BADGE[r.state];
                  const override = Math.round(Number(r.amount)) !== Math.round(Number(r.standard_amount));
                  const msLeft = new Date(r.expires_at).getTime() - now;
                  return (
                    <tr key={r.id}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <div style={strong}>{fmtDateTime(r.created_at)}</div>
                        <div style={sub}>{r.created_by}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={strong}>{r.customer_name || <span style={{ color: 'var(--color-ink-faint)', fontWeight: 500 }}>No name on file</span>}</div>
                        <div style={{ ...sub, fontVariantNumeric: 'tabular-nums' }}>{r.phone}</div>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ ...strong, fontVariantNumeric: 'tabular-nums' }}>{toDisplayDate(r.booking_date)}</div>
                        <div style={sub}>{r.cruise_name} · {r.deck}</div>
                      </td>
                      <td style={{ ...tdStyle, ...strong, whiteSpace: 'nowrap' }}>{r.table_names.join(', ')}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{guestsText(r)}</td>
                      <td style={tdNum}>
                        <div style={{ ...strong, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {override && <Badge tone="amber">Override</Badge>}{inr(r.amount)}
                        </div>
                        {override && (
                          <div style={{ ...sub, whiteSpace: 'normal', textAlign: 'right', maxWidth: 220, marginLeft: 'auto' }}>
                            Standard {inr(r.standard_amount)}{r.override_reason ? ` — ${r.override_reason}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {r.state === 'live' && (
                          <>
                            <div style={{ fontWeight: 700, color: countdownColor(msLeft) }}>{countdown(msLeft)}</div>
                            <div style={sub}>until {fmtTime(r.expires_at)}</div>
                          </>
                        )}
                        {r.state === 'expired' && <div style={strong}>{fmtDateTime(r.expires_at)}</div>}
                        {r.state === 'used' && <div style={strong}>{fmtDateTime(r.used_at) || '—'}</div>}
                        {r.state === 'cancelled' && (
                          <>
                            <div style={strong}>{fmtDateTime(r.cancelled_at) || '—'}</div>
                            {r.cancelled_by && <div style={sub}>{r.cancelled_by}</div>}
                          </>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}><Badge tone={badge.tone}>{badge.label}</Badge></td>
                      <td style={{ ...tdStyle, padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {r.state !== 'used' && (
                          <div style={{ display: 'inline-flex', gap: 4 }}>
                            <Button variant="ghost" size="sm" onClick={() => startReissue(r)} aria-label={`Reissue link for ${r.customer_name || r.phone}`}>
                              <RotateCcw size={14} strokeWidth={1.75} aria-hidden="true" />Reissue
                            </Button>
                            {r.state === 'live' && (
                              <Button variant="ghost" size="sm" onClick={() => setConfirm({ action: 'cancel', row: r })} aria-label={`Cancel link for ${r.customer_name || r.phone}`} style={{ color: 'var(--color-danger-strong)' }}>
                                <Ban size={14} strokeWidth={1.75} aria-hidden="true" />Cancel
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation — names the customer and the tables; this kills a sale in progress */}
      {confirm && (
        <div className="ss-modal-backdrop" onClick={() => { if (!busy) { setConfirm(null); setConfirmError(''); } }}>
          <div className="ss-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="tl-confirm-title" style={{ maxWidth: 460 }}>
            <div style={{ width: 44, height: 44, background: 'var(--color-warning-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <AlertTriangle size={22} strokeWidth={2} color="var(--color-warning-strong)" aria-hidden="true" />
            </div>
            <h3 id="tl-confirm-title" style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, marginBottom: 6 }}>
              {confirm.action === 'cancel' ? 'Cancel this link?' : 'Cancel and reissue this link?'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-ink-brand-softer)', marginBottom: 8, lineHeight: 1.45 }}>
              <strong>{describe(confirm.row)}</strong>
            </p>
            <p style={{ fontSize: '0.82rem', color: 'var(--color-ink-muted)', marginBottom: 16, lineHeight: 1.45 }}>
              {confirm.row.state === 'live'
                ? 'The link stops working and the tables are released immediately. If the customer is paying right now, that payment will not book and will need a refund.'
                : 'The link has already lapsed; this marks it cancelled.'}
              {confirm.action === 'reissue' && ' You will then send a fresh link with the same details and a new expiry.'}
            </p>
            {confirmError && (
              <div className="toast toast-error" role="alert" style={{ marginBottom: 12 }}>{confirmError}</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant={confirm.action === 'cancel' ? 'danger' : 'primary'} onClick={runConfirm} disabled={busy}>
                {busy ? 'Cancelling…' : confirm.action === 'cancel' ? 'Cancel link' : 'Cancel and reissue'}
              </Button>
              <Button variant="secondary" onClick={() => { setConfirm(null); setConfirmError(''); }} disabled={busy}>Keep link</Button>
            </div>
          </div>
        </div>
      )}

      {/* Reissue — the seating sheet's own modal, prefilled, fresh expiry */}
      {reissue && (
        <SendLinkModal
          selectedTables={reissue.table_names}
          displayDate={toDisplayDate(reissue.booking_date)}
          inputDate={reissue.booking_date}
          cruise={reissue.cruise_name}
          deck={reissue.deck}
          initial={prefillFor(reissue)}
          onCreated={() => load()}
          onClose={() => { setReissue(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Full-screen panel over the seating sheet ────────────────────────────────
// A view, not a mode: it covers the sheet (and the nav) while open and
// unmounts on close, leaving the sheet's date, cruise and selection untouched.
// Its own dialogs (cancel confirm, reissue modal) use .ss-modal-backdrop at
// z-index 1500, above the panel's 1400. Escape closes the panel — unless one
// of those dialogs is open, in which case Escape is theirs.
export function TableLinksPanel({ onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';            // the sheet must not scroll under the panel
    panelRef.current?.querySelector('[data-autofocus]')?.focus();
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (panelRef.current?.querySelector('.ss-modal-backdrop')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div ref={panelRef} className="ss-links-panel" role="dialog" aria-modal="true" aria-label="Table links">
      <div className="ss-links-panel-inner">
        <TableLinks onClose={onClose} />
      </div>
    </div>
  );
}
